import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Globe, ExternalLink, FileText, 
  ChevronDown, ChevronUp, Shield, Link2
} from "lucide-react";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TouchTypeBadge({ type }) {
  const styles = {
    first_touch: "bg-emerald-500/20 text-emerald-300",
    duplicate_touch: "bg-amber-500/20 text-amber-300",
    accepted_duplicate: "bg-blue-500/20 text-blue-300",
  };
  
  const labels = {
    first_touch: "First Touch",
    duplicate_touch: "Duplicate Touch",
    accepted_duplicate: "Accepted Duplicate",
  };
  
  return (
    <Badge className={styles[type] || "bg-slate-500/20 text-slate-300"}>
      {labels[type] || type}
    </Badge>
  );
}

function DataRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-white/5 last:border-0">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="col-span-2 text-sm text-slate-200 break-all">{value}</div>
    </div>
  );
}

function DataSection({ title, icon: Icon, children }) {
  const hasContent = React.Children.toArray(children).some(child => child !== null);
  if (!hasContent) return null;
  
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <span className="text-sm font-medium text-slate-300">{title}</span>
      </div>
      <div className="pl-6 space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function TouchEventCard({ touch, isExpanded, onToggle }) {
  return (
    <div className="rounded-lg bg-white/5 ring-1 ring-white/10 overflow-hidden" data-testid={`touch-event-${touch.id}`}>
      {/* Header - always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <TouchTypeBadge type={touch.touch_type} />
          <span className="text-sm text-slate-200">{formatDateTime(touch.received_at)}</span>
          {/* Compliance status badge */}
          {touch.call_text_eligible === true && (
            <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">Eligible</Badge>
          )}
          {touch.call_text_eligible === false && (
            <Badge className="bg-red-500/20 text-red-300 text-xs">{touch.compliance_status || "Not Eligible"}</Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {touch.lead_source_name && (
            <span className="text-xs text-slate-400">{touch.lead_source_name}</span>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-3">
          {/* Quick summary */}
          {(touch.meta_campaign_name || touch.utm_source || touch.vertical_key) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {touch.vertical_key && (
                <Badge variant="outline" className="bg-purple-500/10 text-purple-200 border-purple-500/20">
                  {touch.vertical_label || touch.vertical_key}
                </Badge>
              )}
              {touch.meta_campaign_name && (
                <Badge variant="outline" className="bg-indigo-500/10 text-indigo-200 border-indigo-500/20">
                  {touch.meta_campaign_name}
                </Badge>
              )}
              {touch.utm_source && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-200 border-emerald-500/20">
                  {touch.utm_source}/{touch.utm_medium || "—"}
                </Badge>
              )}
            </div>
          )}
          
          {/* Vertical/Routing */}
          {(touch.vertical_key || touch.vertical_label) && (
            <DataSection title="Routing" icon={Link2}>
              <DataRow label="Vertical Key" value={touch.vertical_key} />
              <DataRow label="Vertical Label" value={touch.vertical_label} />
            </DataSection>
          )}
          
          {/* UTM Parameters */}
          <DataSection title="UTM Tracking" icon={Link2}>
            <DataRow label="Source" value={touch.utm_source} />
            <DataRow label="Medium" value={touch.utm_medium} />
            <DataRow label="Campaign" value={touch.utm_campaign} />
            <DataRow label="Term" value={touch.utm_term} />
            <DataRow label="Content" value={touch.utm_content} />
            <DataRow label="fbclid" value={touch.fbclid} />
            <DataRow label="gclid" value={touch.gclid} />
          </DataSection>
          
          {/* Page/Referrer */}
          <DataSection title="Page & Referrer" icon={Globe}>
            <DataRow label="Page URL" value={touch.page_url} />
            <DataRow label="Referrer URL" value={touch.referrer_url} />
          </DataSection>
          
          {/* GHL Integration */}
          <DataSection title="GoHighLevel" icon={ExternalLink}>
            <DataRow label="Contact ID" value={touch.ghl_contact_id} />
            <DataRow label="Location ID" value={touch.ghl_location_id} />
            <DataRow label="Workflow ID" value={touch.ghl_workflow_id} />
            <DataRow label="Workflow Name" value={touch.ghl_workflow_name} />
            <DataRow label="Workflow Key" value={touch.ghl_workflow_key} />
            <DataRow label="Trigger ID" value={touch.ghl_trigger_id} />
            <DataRow label="Trigger Name" value={touch.ghl_trigger_name} />
            <DataRow label="Form ID" value={touch.ghl_form_id} />
            <DataRow label="Form Name" value={touch.ghl_form_name} />
            <DataRow label="Funnel Key" value={touch.ghl_funnel_key} />
          </DataSection>
          
          {/* Consent & Compliance */}
          <DataSection title="Consent & Compliance" icon={Shield}>
            <DataRow label="Consent Given" value={touch.consent_given === true ? (touch.consent_was_derived ? "Yes (derived)" : "Yes") : touch.consent_given === false ? "No" : null} />
            {touch.consent_was_derived && (
              <DataRow label="Consent Source" value="Derived from consent bundle (text + version + timestamp)" />
            )}
            <DataRow label="TCPA Consent" value={touch.consent_tcpa === true ? "Yes" : touch.consent_tcpa === false ? "No" : null} />
            <DataRow label="Marketing Consent" value={touch.consent_marketing === true ? "Yes" : touch.consent_marketing === false ? "No" : null} />
            <DataRow label="Consent Version" value={touch.consent_version} />
            <DataRow label="Consent Timestamp" value={touch.consent_timestamp} />
            <DataRow label="Consent Text" value={touch.consent_text} />
            <DataRow label="Consent Text Hash" value={touch.consent_text_hash} />
            <DataRow label="Lead IP Address" value={touch.lead_ip_address} />
            <DataRow label="IP Source" value={touch.lead_ip_source} />
            <DataRow label="IP Confidence" value={touch.lead_ip_confidence} />
            <DataRow label="User Agent" value={touch.user_agent} />
          </DataSection>
          
          {/* Gating Status */}
          <DataSection title="Gating Status" icon={Shield}>
            <DataRow label="Call/Text Eligible" value={touch.call_text_eligible === true ? "Yes" : touch.call_text_eligible === false ? "No" : null} />
            <DataRow label="Compliance Status" value={touch.compliance_status} />
            <DataRow label="Payload Valid" value={touch.payload_valid === true ? "Yes" : touch.payload_valid === false ? "No" : null} />
            {touch.validation_errors?.length > 0 && (
              <DataRow label="Validation Errors" value={touch.validation_errors.join(", ")} />
            )}
            <DataRow label="GHL Callback Sent" value={touch.ghl_callback_sent === true ? "Yes" : touch.ghl_callback_sent === false ? "No" : null} />
            <DataRow label="GHL Callback Status" value={touch.ghl_callback_status} />
          </DataSection>
          
          {/* Metadata */}
          {(touch.matched_on || touch.intake_event_id) && (
            <DataSection title="Metadata" icon={FileText}>
              <DataRow label="Matched On" value={touch.matched_on} />
              <DataRow label="Intake Event ID" value={touch.intake_event_id} />
              <DataRow label="Notes" value={touch.notes} />
            </DataSection>
          )}
        </div>
      )}
    </div>
  );
}

export default function TouchHistoryModal({ open, onOpenChange, leadId, leadName }) {
  const [touches, setTouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    if (!open || !leadId) return;
    
    const fetchTouches = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/leads/${leadId}/touches?limit=50`);
        setTouches(res.data.touches || []);
        // Auto-expand the first touch
        if (res.data.touches?.length > 0) {
          setExpandedIds(new Set([res.data.touches[0].id]));
        }
      } catch (e) {
        console.error("Error fetching touches:", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTouches();
  }, [open, leadId]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(touches.map((t) => t.id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl max-h-[85vh]"
        data-testid="touch-history-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-8">
            <span>Touch History {leadName && `— ${leadName}`}</span>
            <Badge variant="secondary" className="bg-indigo-500/20 text-indigo-200">
              {touches.length} {touches.length === 1 ? "touch" : "touches"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {!loading && touches.length > 1 && (
          <div className="flex justify-end gap-2 -mt-2 mb-2">
            <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs text-slate-400 hover:text-slate-200">
              Expand All
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs text-slate-400 hover:text-slate-200">
              Collapse All
            </Button>
          </div>
        )}

        <ScrollArea className="max-h-[60vh] pr-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-16 bg-white/5 rounded-lg" />
              ))}
            </div>
          ) : touches.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No touch history available
            </div>
          ) : (
            <div className="space-y-3">
              {touches.map((touch) => (
                <TouchEventCard
                  key={touch.id}
                  touch={touch}
                  isExpanded={expandedIds.has(touch.id)}
                  onToggle={() => toggleExpand(touch.id)}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
